import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AnalyticalPanel, ChartHeader, DataQualityState, Table } from '@atlas/ui';
import { api } from '../../lib/api';
import { ExplainActions, PageHeader } from '../../components/shared';
import { MetricExplainer } from '../../components/explain';
import { PageSkeleton } from '../../components/PageSkeleton';
import { useEvidence } from '../../components/EvidenceContext';

export function MarketsPage() {
  const { openEvidence } = useEvidence();
  const [cursor, setCursor] = useState(0);
  const { data, isLoading, isError } = useQuery({ queryKey: ['panels'], queryFn: api.panels });

  if (isLoading) return <PageSkeleton blocks={6} />;
  if (isError || !data) return <DataQualityState state="unavailable" title="Market panels unavailable" />;

  const panels = data.panels;
  const activePanel = panels[cursor];

  return (
    <div className="space-y-6">
      <PageHeader title="Markets" subtitle="Six synchronized analytical panels." />
      <ExplainActions screenName="Markets" />

      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {panels.map((panel, i) => (
          <button
            key={panel.panelId}
            type="button"
            className={`markets-cursor ${cursor === i ? 'is-active' : ''}`}
            onClick={() => setCursor(i)}
          >
            {panel.panelId}
          </button>
        ))}
        <time className="ml-auto text-xs text-[var(--color-text-muted)]">
          As of {data.asOf?.slice(0, 10)}
        </time>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {panels.map((panel) => (
          <AnalyticalPanel key={panel.panelId} title={panel.title} subtitle={panel.headline}>
            <ChartHeader
              title={panel.headline}
              source={panel.series?.[0]?.sourceId ?? 'Fixture'}
              status={panel.status === 'complete' ? 'fresh' : 'partial'}
              onExplain={() => openEvidence(panel.panelId, panel.headline)}
              onViewData={() =>
                openEvidence(
                  'Data table',
                  panel.series?.map((s) => `${s.label}: ${s.points?.[0]?.value ?? '—'}`).join('\n') ?? '',
                )
              }
              onViewSources={() =>
                openEvidence('Sources', panel.series?.map((s) => s.sourceId).join(', ') ?? '')
              }
            />
            {(panel.series ?? []).slice(0, 2).map((s) => (
              <MetricExplainer
                key={s.seriesId}
                label={s.label}
                value={String(s.points?.[0]?.value ?? '—')}
                unit={s.unit}
                glossaryTermId={s.glossaryTermId}
                status={s.status === 'complete' ? 'fresh' : 'partial'}
                explainBody={`${s.label} from ${s.sourceId}`}
              />
            ))}
            {panel.status !== 'complete' && (
              <DataQualityState state="partial" className="mt-3" />
            )}
          </AnalyticalPanel>
        ))}
      </div>

      {activePanel && (
        <AnalyticalPanel title="Focused panel" subtitle={activePanel.title}>
          <p className="text-sm text-[var(--color-text-secondary)]">{activePanel.headline}</p>
        </AnalyticalPanel>
      )}

      <AnalyticalPanel title="Accessible data view">
        <Table
          caption="Market panel summary"
          rows={panels}
          getRowKey={(r) => r.panelId}
          columns={[
            { key: 'id', header: 'Panel', cell: (r) => r.panelId },
            { key: 'metric', header: 'Series', cell: (r) => r.series?.[0]?.label ?? '—' },
            { key: 'value', header: 'Value', cell: (r) => r.series?.[0]?.points?.[0]?.value ?? '—', align: 'right' },
            { key: 'status', header: 'Status', cell: (r) => r.status },
          ]}
        />
      </AnalyticalPanel>
    </div>
  );
}
